/**
 * Formatting utility functions
 */

/** IANA timezone for Slovakia (Bratislava). Used for consistent server/client date output. */
export const FORMAT_DATE_TIMEZONE = 'Europe/Bratislava' as const

const dateFormatterCache = new Map<string, Intl.DateTimeFormat>()

function getDateFormatter(locale: string): Intl.DateTimeFormat {
	const cached = dateFormatterCache.get(locale)
	if (cached) return cached
	const formatter = new Intl.DateTimeFormat(locale, {
		timeZone: FORMAT_DATE_TIMEZONE,
		year: 'numeric',
		month: '2-digit',
		day: '2-digit'
	})
	dateFormatterCache.set(locale, formatter)
	return formatter
}

/**
 * Format a calendar date in the given locale, always interpreted in Europe/Bratislava.
 * Uses `Intl.DateTimeFormat` (SSR-safe in Node.js and browsers).
 */
export function formatDate(date: Date | string, locale = 'sk-SK'): string {
	const dateObj = typeof date === 'string' ? new Date(date) : date
	return getDateFormatter(locale).format(dateObj)
}

/**
 * Format a number with thousands separator
 */
export function formatNumber(num: number, locale = 'sk-SK'): string {
  return num.toLocaleString(locale)
}

/**
 * Truncate text with ellipsis
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text
  return text.slice(0, maxLength - 3) + '...'
}

/**
 * Remove <p> tags from HTML string (for headings)
 * Preserves inner HTML content and other tags like <strong>, <em>, etc.
 * Converts <p> tags to <br> for line breaks between paragraphs
 * 
 * @param html - HTML string that may contain <p> tags
 * @returns HTML string with <p> tags removed
 * 
 * @example
 * stripParagraphTags('<p>Hello <strong>world</strong></p><p>Second line</p>')
 * // Returns: 'Hello <strong>world</strong><br>Second line'
 */
export function stripParagraphTags(html: string): string {
  if (!html || typeof html !== 'string') return html
  
  // Replace closing </p> followed by opening <p> with <br> for line breaks
  let result = html.replace(/<\/p>\s*<p>/gi, '<br>')
  
  // Remove opening <p> tags (with any attributes)
  result = result.replace(/<p[^>]*>/gi, '')
  
  // Remove closing </p> tags
  result = result.replace(/<\/p>/gi, '')
  
  // Clean up any leading/trailing <br> tags
  result = result.replace(/^(<br\s*\/?>)+|(<br\s*\/?>)+$/gi, '')
  
  return result.trim()
}
